import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  emailToCardAddressViewSchema,
  type ApiError,
  type EmailToCardAddressView,
} from '@molt/shared';
import { get, post } from './api';
import { queryKeys } from './query-keys';

export function getEmailToCardAddress(cardId: string): Promise<EmailToCardAddressView> {
  return get(
    `/cards/${encodeURIComponent(cardId)}/email-to-card`,
    emailToCardAddressViewSchema,
  );
}

export function regenerateEmailToCardAddress(
  cardId: string,
): Promise<EmailToCardAddressView> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/email-to-card/regenerate`,
    emailToCardAddressViewSchema,
    {},
  );
}

export function emailToCardAddressQueryOptions(cardId: string, enabled = true) {
  return queryOptions({
    queryKey: queryKeys.cards.emailToCard(cardId),
    queryFn: () => getEmailToCardAddress(cardId),
    enabled,
  });
}

export function useEmailToCardAddress(
  cardId: string,
  enabled = true,
): UseQueryResult<EmailToCardAddressView, ApiError> {
  return useQuery<EmailToCardAddressView, ApiError>({
    queryKey: queryKeys.cards.emailToCard(cardId),
    queryFn: () => getEmailToCardAddress(cardId),
    enabled,
  });
}

export function useRegenerateEmailToCardAddress(
  cardId: string,
): UseMutationResult<EmailToCardAddressView, ApiError, void> {
  const queryClient = useQueryClient();

  return useMutation<EmailToCardAddressView, ApiError, void>({
    mutationFn: () => regenerateEmailToCardAddress(cardId),
    onSuccess: (address) => {
      queryClient.setQueryData(queryKeys.cards.emailToCard(address.cardId), address);
    },
  });
}
